"use client"

import { useEffect, useRef } from "react"
import { trackSectionView } from "@/lib/analytics"

interface SectionTrackerProps {
    sectionName: string
    children: React.ReactNode
}

/**
 * Component that tracks when a section comes into view
 */
export function SectionTracker({ sectionName, children }: SectionTrackerProps) {
    const sectionRef = useRef<HTMLDivElement>(null)
    const hasTracked = useRef(false)

    useEffect(() => {
        const element = sectionRef.current
        if (!element) return

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    // Calculate if section is taking up significant viewport space (for tall sections)
                    const viewportHeight = window.innerHeight
                    const isTallAndVisible =
                        entry.intersectionRect.height >= viewportHeight * 0.5 &&
                        entry.intersectionRatio >= 0.2

                    // Track when section is 50% visible OR significant portion of tall section is visible
                    if (
                        entry.isIntersecting &&
                        !hasTracked.current &&
                        (entry.intersectionRatio >= 0.5 || isTallAndVisible)
                    ) {
                        trackSectionView(sectionName)
                        hasTracked.current = true
                    }
                })
            },
            {
                threshold: [0.2, 0.5], // Trigger at 20% (for tall sections) and 50%
                rootMargin: "-50px 0px", // Offset to account for fixed headers
            }
        )

        observer.observe(element)

        return () => {
            observer.disconnect()
        }
    }, [sectionName])

    return <div ref={sectionRef}>{children}</div>
}
